import { useAuth } from '@/src/context/AuthContext';
import { useTheme } from '@/src/context/ThemeContext';
import { getPublicPostUseCase } from '@/src/core/dependencies';
import { PublicPost } from '@/src/domain/models';
import { useTranslation } from '@/src/i18n';
import { PostDetailScreen } from '@/src/presentation/screens/PostDetailScreen';
import { logger } from '@/src/core/utils';
import Head from 'expo-router/head';
import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import { useEffect, useState } from 'react';
import { ActivityIndicator, Platform, StyleSheet, Text, View } from 'react-native';

export default function PublicPostContainer() {
  const router = useRouter();
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { user } = useAuth();
  const { id } = useLocalSearchParams<{ id: string }>();
  const isAuthenticated = !!user;

  const [post, setPost] = useState<PublicPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadPost();
  }, [id]);

  const loadPost = async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      const data = await getPublicPostUseCase.execute(id);
      if (!data) {
        setError(t('post.notFound'));
        return;
      }
      setPost(data);
    } catch (err) {
      logger.error('PublicPost', 'Failed to load public post', { error: err });
      setError(t('post.notFound'));
    } finally {
      setLoading(false);
    }
  };

  const handleTournamentPress = (tournamentId: string) => {
    router.push(`/public/${tournamentId}`);
  };

  const postUrl = `https://tournaly.app/post/${id}`;

  const seoTitle = post
    ? `${post.userName ?? 'Tournaly'} — ${post.content.slice(0, 60)}${post.content.length > 60 ? '…' : ''}`
    : 'Post — Tournaly';

  const seoDescription = post
    ? post.content.slice(0, 160)
    : t('post.notFound');

  // JSON-LD for social sharing preview
  useEffect(() => {
    if (Platform.OS !== 'web' || !post) return;
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'post-jsonld';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'SocialMediaPosting',
      headline: post.content.slice(0, 110),
      text: post.content,
      url: postUrl,
      datePublished: post.createdAt.toISOString(),
      author: {
        '@type': 'Person',
        name: post.userName ?? 'Tournaly User',
      },
      ...(post.imageUrl && { image: post.imageUrl }),
      ...(post.tournamentName && {
        about: { '@type': 'Event', name: post.tournamentName },
      }),
    });
    document.getElementById('post-jsonld')?.remove();
    document.head.appendChild(script);
    return () => { script.remove(); };
  }, [post, postUrl]);

  if (loading) {
    return (
      <View style={[styles.center, { backgroundColor: colors.background }]}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  if (error || !post) {
    return (
      <View style={[styles.center, { backgroundColor: colors.background }]}>
        <Text style={[styles.errorText, { color: colors.error }]}>{t('post.notFound')}</Text>
        <Text style={[styles.errorSubtext, { color: colors.textSecondary }]}>
          {t('post.notPublicSubtext')}
        </Text>
      </View>
    );
  }

  return (
    <>
      <Head>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <link rel="canonical" href={postUrl} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:url" content={postUrl} />
        <meta property="og:type" content="article" />
        {post.imageUrl && <meta property="og:image" content={post.imageUrl} />}
        <meta name="twitter:card" content={post.imageUrl ? 'summary_large_image' : 'summary'} />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        {post.imageUrl && <meta name="twitter:image" content={post.imageUrl} />}
      </Head>
      <Stack.Screen options={{ headerShown: isAuthenticated, title: t('post.screenTitle') }} />
      <PostDetailScreen
        post={post}
        postUrl={postUrl}
        colors={colors}
        isAuthenticated={isAuthenticated}
        onTournamentPress={handleTournamentPress}
      />
    </>
  );
}

const styles = StyleSheet.create({
  center: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  errorText: {
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 8,
    textAlign: 'center',
  },
  errorSubtext: {
    fontSize: 14,
    textAlign: 'center',
  },
});
